import {User} from "../../../../../api/accessControl/User.ts";
import {useCallback, useEffect, useState, useMemo, useRef} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {BackendApi} from "../../../../../api/api.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import ClientSharedDocumentsTable from "./ClientSharedDocumentsTable.tsx";
import {usePartnerClientScope} from "../../../../../hooks/usePartnerClientScope.ts";
import {ClientSubtitle} from "../../../../../components/specific/ClientSwitcher";
import {PartnerClientToolbar, getVisibleGoToItemIds} from "../../../../../components/specific/PartnerClientToolbar";
import {
    DocumentFilters,
    DocumentsResponse,
    FilterOptions,
    PartnerBill,
    PartnerBillOrCreditCardStatement,
    PartnerDocumentsRepository,
    SortingState,
} from "./partnerDocuments";
import {useNotifications} from "../../../../../lib/NotificationContext.tsx";
import {documentConflictMessage} from "../../../../../lib/apiErrors.ts";
import {DOCUMENT_CLIENT_PARAM} from "../../../../../lib/documentDeepLink.ts";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {
    useDocumentDrawer,
    useOpenDocument,
} from "../../../../../views/DocumentDrawer";
import {useDocument} from "../../../../../views/DocumentDrawer/hooks/documentDrawerApi.ts";
import {useFilterStore} from "../../../../../contexts/FilterStore.tsx";
import {QueryKey} from "../../../../../api/queryKeys.ts";
import {
    usePartner__client__document__deleteMutation,
    usePartner__document__bill__markAsPrivatelyPaidMutation,
    usePartner__document__bill__undoMarkAsPrivatelyPaidMutation,
    usePartner__document__toggleDocumentVatIgnoredMutation,
} from "../../../../../generated/blueprint.ts";
import {FixedAssetPreviewPopup} from "../../../../../components/specific/FixedAssetPreview";
import {useFixedAssetDetails} from "../../../../../hooks/useFixedAssetDetails.ts";
import {LINK_FIXED_ASSETS_FLAG} from "../partnerDashboardPage/components/FixedAssets/linkFixedAssetsFlag.ts";
import {DeepLinkedDocument, deepLinkFailureMessage, findRouteDocument} from "./routeDocument.ts";

const NO_DOCUMENTS: PartnerBillOrCreditCardStatement[] = [];
const NO_RELATIONS: FilterOptions['relations'] = [];

export default function PartnerDocumentsPage({currentUser}: { currentUser: User }) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const { state } = useFilterStore();

    const clientScope = usePartnerClientScope(currentUser);

    const filters = useMemo(() => ({
        dateFrom: state.common.dateRange.from,
        dateTo: state.common.dateRange.to,
        dateType: state.documents.dateType,
        customerId: clientScope.selectedClientId,
        relations: state.documents.relations,
        documentDirection: state.documents.documentDirection,
        documentCategories: state.documents.documentCategories,
        tags: state.documents.tags,
        tagsMode: state.documents.tagsMode
    } as DocumentFilters), [state, clientScope.selectedClientId]);

    const [sortingState, setSortingState] = useState<SortingState>({orderby: "relation_name", order: "asc"});

    const queryClient = useQueryClient();
    const {dispatch: notifyDispatch} = useNotifications();

    const documentDrawer = useDocumentDrawer();
    const navigate = useNavigate();
    const location = useLocation();
    const {documentId: routeDocumentId} = useParams();
    const [searchParams] = useSearchParams();
    const routeClientId = searchParams.get(DOCUMENT_CLIENT_PARAM);
    const openDocumentId = useOpenDocument()?.documentId ?? null;
    const openedFromRoute = useRef(false);
    const drawerWasOpen = useRef(false);
    const [deepLinkedDocument, setDeepLinkedDocument] = useState<DeepLinkedDocument | null>(null);
    const [deepLinkFailure, setDeepLinkFailure] = useState<string | null>(null);
    const deepLinkedDocumentQuery = useDocument(
        deepLinkedDocument?.businessId ?? null,
        deepLinkedDocument?.documentId ?? null,
    );

    const canSeeFixedAssets = currentUser.flags.getFlag(LINK_FIXED_ASSETS_FLAG);
    const [previewedFixedAsset, setPreviewedFixedAsset] = useState<{ businessId: string; fixedAssetId: string } | null>(null);
    const fixedAssetDetails = useFixedAssetDetails(
        previewedFixedAsset?.businessId ?? null,
        previewedFixedAsset?.fixedAssetId ?? null,
    );
    const handleFixedAssetClick = useCallback((bill: PartnerBill) => {
        if (bill.fixed_asset_id) {
            setPreviewedFixedAsset({businessId: bill.business_id, fixedAssetId: bill.fixed_asset_id});
        }
    }, []);

    const documentsQuery = useQuery({
        queryKey: [QueryKey.Documents, filters, sortingState],
        queryFn: async ({signal}): Promise<DocumentsResponse> => {

            return await PartnerDocumentsRepository.fetchAllDocuments(
                api.withAbortSignal(signal),
                filters,
                sortingState,
            );
        },
        refetchOnWindowFocus: false,
        gcTime: 0,
    });

    const documents = documentsQuery.data?.documents ?? NO_DOCUMENTS;

    const lastRelations = useRef<FilterOptions['relations']>(NO_RELATIONS);
    if (documentsQuery.data?.filter_options) {
        lastRelations.current = documentsQuery.data.filter_options.relations;
    }
    const relations = lastRelations.current;
    const filterOptions: FilterOptions = useMemo(
        () => ({relations}),
        [relations],
    );

    const refreshDocuments = useCallback(
        () => queryClient.invalidateQueries({queryKey: [QueryKey.Documents]}),
        [queryClient],
    );

    const {mutateAsync: toggleVat} = usePartner__document__toggleDocumentVatIgnoredMutation(api, {invalidates: [QueryKey.Documents]});
    const {mutateAsync: deleteDocument} = usePartner__client__document__deleteMutation(api, {invalidates: [QueryKey.Documents]});
    const {mutateAsync: markPrivatelyPaid} = usePartner__document__bill__markAsPrivatelyPaidMutation(api, {invalidates: [QueryKey.Documents]});
    const {mutateAsync: undoPrivatelyPaid} = usePartner__document__bill__undoMarkAsPrivatelyPaidMutation(api, {invalidates: [QueryKey.Documents]});

    const drawerDocuments = useMemo(
        () => documents.map((doc) => ({
            id: doc.id,
            businessId: doc.business_id,
            fileName: doc.file_name,
        })),
        [documents],
    );

    useEffect(() => {
        if (openDocumentId) {
            drawerWasOpen.current = true;
            return;
        }
        if (drawerWasOpen.current) {
            drawerWasOpen.current = false;
            void refreshDocuments();
        }
    }, [openDocumentId, refreshDocuments]);

    const handleVatStatusChange = useCallback(async (document: PartnerBill) => {
        try {
            await toggleVat({document_id: document.id});
        } catch (error) {
            console.log("Error toggling VAT status:", error);
            alert("something went wrong when changing vat status");
            throw error;
        }
    }, [toggleVat]);

    const handleDelete = useCallback(async (document: PartnerBillOrCreditCardStatement) => {
        try {
            await deleteDocument({
                document_id: document.id,
            });
            notifyDispatch({
                title: 'Document verwijderd',
                description: 'Het document is succesvol verwijderd',
                type: 'success',
            });
        } catch (error) {
            console.error("Error deleting document:", error);
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                alert(conflictMessage);
            } else {
                notifyDispatch({
                    title: 'Fout bij verwijderen',
                    description: 'Er is een fout opgetreden bij het verwijderen van het document. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
            throw error;
        }
    }, [deleteDocument, notifyDispatch]);

    const handleUndoPrivatelyPaid = useCallback(async (document: PartnerBill) => {
        try {
            await undoPrivatelyPaid({document_id: document.id});
            notifyDispatch({
                title: 'Privé betaald ongedaan gemaakt',
                description: 'Het document staat weer open en kan worden gekoppeld.',
                type: 'success',
            });
        } catch (error) {
            console.error("Error undoing privately paid:", error);
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                alert(conflictMessage);
            } else {
                notifyDispatch({
                    title: 'Fout bij ongedaan maken',
                    description: 'Er is een fout opgetreden. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
            throw error;
        }
    }, [undoPrivatelyPaid, notifyDispatch]);

    const handleMarkAsPrivatelyPaid = useCallback(async (document: PartnerBill) => {
        try {
            await markPrivatelyPaid({document_id: document.id});
            notifyDispatch({
                title: 'Document gemarkeerd als privé betaald',
                description: 'Het document is succesvol uitgesloten van matching.',
                type: 'success',
            });
        } catch (error) {
            console.error("Error marking document as privately paid:", error);
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                alert(conflictMessage);
            } else {
                notifyDispatch({
                    title: 'Fout bij markeren als privé betaald',
                    description: 'Er is een fout opgetreden bij het markeren van het document. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
            throw error;
        }
    }, [markPrivatelyPaid, notifyDispatch]);

    const handleSortingChange = useCallback((state: SortingState) => {
        setSortingState(state);
    }, []);

    useEffect(() => {
        if (openedFromRoute.current || documentsQuery.isPending || documentsQuery.isError || !routeDocumentId) {
            return;
        }

        const routeDocument = findRouteDocument(routeDocumentId, routeClientId, documents);
        switch (routeDocument.kind) {
            case "listed":
                documentDrawer.open(routeDocument.document.business_id, routeDocument.document.id, {
                    documentList: drawerDocuments,
                });
                break;
            case "unlisted":
                setDeepLinkFailure(null);
                setDeepLinkedDocument(routeDocument.target);
                break;
            case "unknown":
                break;
        }
        openedFromRoute.current = true;
    }, [
        documentsQuery.isPending,
        documentsQuery.isError,
        routeDocumentId,
        routeClientId,
        documents,
        drawerDocuments,
        documentDrawer,
    ]);

    useEffect(() => {
        if (!deepLinkedDocument) {
            return;
        }
        if (deepLinkedDocumentQuery.isSuccess) {
            documentDrawer.open(deepLinkedDocument.businessId, deepLinkedDocument.documentId, {
                documentList: drawerDocuments,
            });
            setDeepLinkedDocument(null);
        } else if (deepLinkedDocumentQuery.isError) {
            setDeepLinkFailure(deepLinkFailureMessage(deepLinkedDocumentQuery.error));
            setDeepLinkedDocument(null);
        }
    }, [
        deepLinkedDocument,
        deepLinkedDocumentQuery.isSuccess,
        deepLinkedDocumentQuery.isError,
        deepLinkedDocumentQuery.error,
        drawerDocuments,
        documentDrawer,
    ]);

    useEffect(() => {
        if (openDocumentId) {
            setDeepLinkFailure(null);
        }
    }, [openDocumentId]);

    useEffect(() => {
        if (deepLinkedDocument || (!openDocumentId && routeDocumentId && !openedFromRoute.current)) {
            return;
        }

        const target = openDocumentId ? `/documents/${openDocumentId}` : '/documents';
        if (location.pathname === target) {
            return;
        }
        navigate(target, {replace: true});
    }, [
        deepLinkedDocument,
        openDocumentId,
        routeDocumentId,
        location.pathname,
        navigate,
    ]);

    return (
        <>
            <Shell
                currentUser={currentUser}
                title="Documenten"
                subtitle={<ClientSubtitle prefix="Documenten van" clientName={clientScope.selectedClient?.name ?? null} fallback="Overzicht van alle documenten van je klanten"/>}
                titleActions={
                    <PartnerClientToolbar
                        clients={clientScope.clients}
                        selectedClientId={clientScope.selectedClientId}
                        recentClientIds={clientScope.recentClientIds}
                        goToItemIds={getVisibleGoToItemIds((flag) => currentUser.flags.getFlag(flag))}
                        onSelectClient={clientScope.selectClient}
                    />
                }
            >
                <div className="w-full">

                    {documentsQuery.isError && (
                        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd"
                                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                              clipRule="evenodd"/>
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-800">
                                        Er zijn geen documenten beschikbaar of u heeft niet genoeg rechten om ze te
                                        bekijken.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {deepLinkFailure && (
                        <div role="alert" className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                            <p className="text-sm text-red-800">{deepLinkFailure}</p>
                        </div>
                    )}

                    <div className="bg-white rounded-lg">
                        <ClientSharedDocumentsTable
                            isLoading={documentsQuery.isPending}
                            documents={documents}
                            sortingState={sortingState}
                            onSortingChange={handleSortingChange}
                            filterOptions={filterOptions}
                            filters={filters}
                            onVatStatusChange={handleVatStatusChange}
                            onRowClick={(documentId, businessId) =>
                                documentDrawer.open(businessId, documentId, {
                                    documentList: drawerDocuments,
                                })
                            }
                            onDelete={handleDelete}
                            onMarkAsPrivatelyPaid={handleMarkAsPrivatelyPaid}
                            onUndoPrivatelyPaid={handleUndoPrivatelyPaid}
                            canSeeFixedAssets={canSeeFixedAssets}
                            onFixedAssetClick={handleFixedAssetClick}
                        />
                    </div>
                    {canSeeFixedAssets && (
                        <FixedAssetPreviewPopup
                            open={previewedFixedAsset !== null}
                            onClose={() => setPreviewedFixedAsset(null)}
                            details={fixedAssetDetails.data}
                            isLoading={fixedAssetDetails.isLoading}
                            isError={fixedAssetDetails.isError}
                            onOpenBill={(documentId) => {
                                const businessId = previewedFixedAsset?.businessId;
                                setPreviewedFixedAsset(null);
                                if (businessId) {
                                    documentDrawer.open(businessId, documentId);
                                }
                            }}
                        />
                    )}
                </div>
            </Shell>
        </>
    );
}
